import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  declareUnavailability,
  deleteUnavailability,
  fetchMyUnavailabilities,
  type DeclareUnavailabilityInput,
  type MyUnavailabilities,
} from '../api/unavailabilitiesApi';

const KEY = 'my-unavailabilities';

/**
 * The technician's own absences plus his derived state. `refetchOnMount` is on for this one: the
 * WebView keeps the page alive across tab switches, and a stale "Available" on a phone is the
 * kind of wrong the whole lot exists to avoid.
 */
export const useMyUnavailabilities = () =>
  useQuery<MyUnavailabilities>({
    queryKey: [KEY],
    queryFn: fetchMyUnavailabilities,
    refetchOnMount: true,
  });

/**
 * A write changes the derived availability as well as the list, and both come from the same
 * request — so the whole key is invalidated rather than the response being patched in. It also
 * moves what the backoffice reads (`technicians`), but that is another session on another screen:
 * invalidating it here would do nothing.
 *
 * Written out twice rather than behind a generic wrapper: the repo already has three of those,
 * byte for byte the same but for the key they invalidate, and a fourth would be one more copy of
 * the abstraction rather than one fewer of the code.
 */
export const useDeclareUnavailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeclareUnavailabilityInput) => declareUnavailability(input),
    // The app-wide default retries mutations once. Replaying a POST whose response was lost but
    // whose write landed comes back `UNAVAILABILITY_OVERLAP` — the screen would then tell the
    // technician his declaration failed when it is saved. Nothing here is idempotent.
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useDeleteUnavailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteUnavailability(id),
    // Same reason: a replayed DELETE meets a 404 and reports a failure for a period that is gone.
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
};
